import * as React from "react";

import { cn } from "../../utils/cn";

import { Button, type ButtonProps } from "./button";

/**
 * `ToggleMenu` — a row (or column) of mutually-exclusive (or
 * multi-select) toggle buttons. Items are rendered as `Button`s so
 * the toggle states reuse the canonical button visual language
 * (variant, size, outline, focus, disabled). The menu owns selection
 * state + ARIA semantics; each `ToggleMenuItem` reads
 * `aria-selected` from context.
 *
 * - **`select="single"`** (default) — exactly one (or zero, if
 *   `allowDeselect`) value is active. Behaves like a radio group.
 * - **`select="multi"`** — any subset can be active. Behaves like
 *   a group of independent toggles.
 *
 * Layout knobs:
 * - **`spacing`** — Tailwind gap scale (0, 1, 2, ...) between items.
 *   Default `2` (8px).
 * - **`layout="fit"`** (default) — items size to their content; the
 *   menu's width is `w-fit`.
 * - **`layout="fill"`** — items stretch with `flex-1` and the menu
 *   takes `w-full`.
 * - **`orientation`** — horizontal or vertical stack.
 *
 * Children must be `<ToggleMenuItem>` elements. Each receives all
 * normal `Button` props (variant, size, outline, leading, trailing,
 * disabled, etc.); `aria-selected` and `onClick` are managed by the
 * menu and should not be passed.
 *
 * @example
 * ```tsx
 * type Mode = "stream" | "claim";
 * const [mode, setMode] = useState<Mode>("stream");
 *
 * <ToggleMenu<Mode>
 *     select="single"
 *     value={mode}
 *     onChange={(next) => next && setMode(next)}
 *     layout="fill"
 *     spacing={2}
 * >
 *     <ToggleMenuItem value="stream" variant="transparent" size="lg" outline>
 *         Stream Directly
 *     </ToggleMenuItem>
 *     <ToggleMenuItem value="claim" variant="transparent" size="lg" outline>
 *         Claim Rewards
 *     </ToggleMenuItem>
 * </ToggleMenu>
 * ```
 */

interface ToggleMenuContextValue {
    select: "single" | "multi";
    layout: "fit" | "fill";
    isSelected: (value: string) => boolean;
    /** Returns false when clicking the item would be a no-op given
     *  the menu's `select` + `allowDeselect` config (e.g., the
     *  current selection in single-select-no-deselect, or the last
     *  active item in multi-select-no-deselect). The item then
     *  renders with `cursor: default` so it doesn't promise an
     *  interaction. */
    isActionable: (value: string) => boolean;
    onItemClick: (value: string) => void;
}

const ToggleMenuContext = React.createContext<ToggleMenuContextValue | null>(
    null
);

interface ToggleMenuCommonProps {
    /** Tailwind gap scale (0, 1, 2, ...) between items. Default 2 (8px). */
    spacing?: number;
    /** `fit` (default) sizes items to content; `fill` stretches each item
     *  with `flex-1` and stretches the menu to `w-full`. */
    layout?: "fit" | "fill";
    /** Direction items are stacked. Default `horizontal`. */
    orientation?: "horizontal" | "vertical";
    /** Class merged onto the menu wrapper. */
    className?: string;
    /** Must be `<ToggleMenuItem>` elements. */
    children: React.ReactNode;
}

type ToggleMenuProps<T extends string = string> =
    | (ToggleMenuCommonProps & {
          select?: "single";
          value: T | null;
          onChange: (value: T | null) => void;
          /** `single` defaults to `false` (clicking the active item
           *  does nothing). Set `true` to allow clearing selection by
           *  re-clicking the active item, in which case `onChange`
           *  fires with `null`. */
          allowDeselect?: boolean;
      })
    | (ToggleMenuCommonProps & {
          select: "multi";
          value: T[];
          onChange: (value: T[]) => void;
          /** `multi` defaults to `true` (the array can become empty).
           *  Set `false` to require at least one item to remain
           *  selected — clicking the last active item is a no-op. */
          allowDeselect?: boolean;
      });

function ToggleMenu<T extends string = string>(
    props: ToggleMenuProps<T>
): React.ReactElement {
    const {
        spacing = 2,
        layout = "fit",
        orientation = "horizontal",
        className,
        children
    } = props;

    const isSingle = (props.select ?? "single") === "single";
    const allowDeselect = props.allowDeselect ?? !isSingle;

    const ctx = React.useMemo<ToggleMenuContextValue>(() => {
        return {
            select: isSingle ? "single" : "multi",
            layout,
            isSelected: (val: string): boolean => {
                if (isSingle) {
                    const single = props as Extract<
                        ToggleMenuProps<T>,
                        { select?: "single" }
                    >;
                    return single.value === (val as T);
                }
                const multi = props as Extract<
                    ToggleMenuProps<T>,
                    { select: "multi" }
                >;
                return multi.value.includes(val as T);
            },
            isActionable: (val: string): boolean => {
                if (isSingle) {
                    const single = props as Extract<
                        ToggleMenuProps<T>,
                        { select?: "single" }
                    >;
                    // Selected + can't deselect → click is a no-op.
                    if (single.value === (val as T) && !allowDeselect) {
                        return false;
                    }
                    return true;
                }
                const multi = props as Extract<
                    ToggleMenuProps<T>,
                    { select: "multi" }
                >;
                // Selected + would-be-deselected + can't go below
                // one selection → click is a no-op.
                if (
                    multi.value.includes(val as T) &&
                    multi.value.length === 1 &&
                    !allowDeselect
                ) {
                    return false;
                }
                return true;
            },
            onItemClick: (val: string): void => {
                if (isSingle) {
                    const single = props as Extract<
                        ToggleMenuProps<T>,
                        { select?: "single" }
                    >;
                    if (single.value === (val as T)) {
                        if (allowDeselect) single.onChange(null);
                    } else {
                        single.onChange(val as T);
                    }
                } else {
                    const multi = props as Extract<
                        ToggleMenuProps<T>,
                        { select: "multi" }
                    >;
                    const current = multi.value;
                    if (current.includes(val as T)) {
                        if (current.length > 1 || allowDeselect) {
                            multi.onChange(
                                current.filter((v) => v !== (val as T))
                            );
                        }
                    } else {
                        multi.onChange([...current, val as T]);
                    }
                }
            }
        };
    }, [props, isSingle, allowDeselect, layout]);

    return (
        <ToggleMenuContext.Provider value={ctx}>
            <div
                role={isSingle ? "radiogroup" : "group"}
                style={
                    {
                        "--toggle-menu-gap": String(spacing)
                    } as React.CSSProperties
                }
                className={cn(
                    "flex",
                    orientation === "vertical" ? "flex-col" : "flex-row",
                    "gap-[calc(var(--toggle-menu-gap)*0.25rem)]",
                    layout === "fill" ? "w-full" : "w-fit",
                    className
                )}
            >
                {children}
            </div>
        </ToggleMenuContext.Provider>
    );
}

interface ToggleMenuItemProps
    extends Omit<ButtonProps, "aria-selected" | "role"> {
    /** Identifier for this item — matched against the menu's `value`
     *  to determine selection state. */
    value: string;
    /* NOTE: `forceHover` / `forcePressed` flow straight through to the inner
     * `Button` via `...rest` (they're part of `ButtonProps`). A scripted sim
     * sets `forceHover` on the specific item the cursor is over to paint its
     * hover VISUAL (toggle variant → ember text + ember border, no fill) with
     * no pointer. That reads distinctly from the SELECTED item, whose visual
     * is the `aria-selected` charcoal fill (value-driven, unchanged here). */
}

function ToggleMenuItem({
    value,
    className,
    onClick,
    ...rest
}: ToggleMenuItemProps): React.ReactElement {
    const ctx = React.useContext(ToggleMenuContext);
    if (!ctx) {
        throw new Error(
            "ToggleMenuItem must be rendered inside a <ToggleMenu>."
        );
    }
    const selected = ctx.isSelected(value);
    const actionable = ctx.isActionable(value);
    return (
        <Button
            {...rest}
            aria-selected={selected ? "true" : undefined}
            role={ctx.select === "single" ? "radio" : "checkbox"}
            onClick={(e) => {
                onClick?.(e);
                ctx.onItemClick(value);
            }}
            className={cn(
                ctx.layout === "fill" && "flex-1",
                // No-op clicks read as non-interactive on hover.
                !actionable && "cursor-default",
                className
            )}
        />
    );
}

export { ToggleMenu, ToggleMenuItem };
export type { ToggleMenuProps, ToggleMenuItemProps };
